import type { TaxTypeDetail } from "../../review/types/multiTax.types";

const SUBTYPE_KEY_SEPARATOR = "::";

/** A sub tax type selection resolved against the tax-type-details catalog. */
export interface ResolvedSubTypeSelection {
    /** The `taxTypeId` the selected sub type belongs to. */
    taxTypeId: string;
    /** The sub type's `source_mapping_id`. */
    sourceMappingId: number;
}

/**
 * Resolves the UI's sub tax type option keys ("taxType::subType") to their owning tax type plus
 * their `sourceMappingId` — the backend's stable per-mapping id, surfaced in the tax-type-details
 * catalog as `taxTypeMappingId`. The tax type comes from the catalog rather than from splitting the
 * key, so ids stay correct even for tax type names that themselves contain the separator.
 *
 * Keys that can't be resolved against the catalog (not loaded yet / stale) are dropped.
 */
export function resolveMultiTaxSubTypeSelections(
    subTypeKeys: string[] | undefined,
    taxTypeDetails: TaxTypeDetail[] | undefined
): ResolvedSubTypeSelection[] {
    if (!subTypeKeys?.length || !taxTypeDetails?.length) {
        return [];
    }
    const selectionByKey = new Map<string, ResolvedSubTypeSelection>();
    taxTypeDetails.forEach(taxType => {
        taxType.taxSubTypeDetails?.forEach(subType => {
            if (subType.taxSubTypeId == null) return;
            if (typeof subType.taxTypeMappingId !== "number") return;
            selectionByKey.set(
                `${taxType.taxTypeId}${SUBTYPE_KEY_SEPARATOR}${subType.taxSubTypeId}`,
                { taxTypeId: taxType.taxTypeId, sourceMappingId: subType.taxTypeMappingId }
            );
        });
    });
    return subTypeKeys
        .map(key => selectionByKey.get(key))
        .filter((selection): selection is ResolvedSubTypeSelection => selection !== undefined);
}

/**
 * The `sourceMappingId`s of the selected sub tax types, in selection order. Thin wrapper over
 * {@link resolveMultiTaxSubTypeSelections} for callers that don't need the owning tax type.
 *
 * Sending the id instead of the name pair lets the search API match on the mapping id rather than
 * fuzzy tax-type/subtype name strings.
 */
export function mapMultiTaxSubTypesToSourceMappingIds(
    subTypeKeys: string[] | undefined,
    taxTypeDetails: TaxTypeDetail[] | undefined
): number[] {
    return resolveMultiTaxSubTypeSelections(subTypeKeys, taxTypeDetails).map(
        selection => selection.sourceMappingId
    );
}
